import type { FeatureConfig } from '@skynet/feature-config';
import { TagsOutlined } from '@ant-design/icons';
import { ITEM_DETAIL_ROUTE_PATH, ITEMS_LIST_URL, ITEMS_ROUTE_PATH } from './routes';

/**
 * Feature manifest for the admin Items list and item detail pages. The dashboard shell reads
 * this single source of truth instead of hardcoding the route, navigation
 * entry, header label and Cerbos action across its routing, navigation,
 * header and access-control modules.
 *
 * `route.load` and `detailRoute.load` are dynamic-import thunks, so importing this config for its
 * nav/header metadata never pulls the page implementation into the main bundle.
 *
 * The route path, nav url and WS-login skip path come from this package's own
 * `./routes`, which also supplies the URLs the page hands to `@skynet/sky-item`'s
 * components. The config imports nothing from `@skynet/sky-item`, so reading it
 * never drags the Items UI onto the boot path.
 * `section`, `nav.key` and `cerbosAction` stay literal: they are the identifier
 * the Cerbos policy is written against (`read:admin:items`), and renaming any
 * of them hides the nav item until the policy is renamed too.
 */
export const itemsConfig = {
  domain: 'Admin',
  section: 'items',
  route: {
    path: ITEMS_ROUTE_PATH,
    load: async () => ({ default: (await import('./Items')).Items }),
  },
  detailRoute: {
    path: ITEM_DETAIL_ROUTE_PATH,
    load: async () => ({ default: (await import('./ItemPage')).ItemPage }),
  },
  nav: {
    key: 'admin.items',
    label: 'Items',
    icon: <TagsOutlined />,
    url: ITEMS_LIST_URL,
  },
  header: { label: 'ITEMS' },
  cerbosAction: 'read:admin:items',
  skipWsLoginPaths: [ITEMS_LIST_URL],
} as const satisfies FeatureConfig;
